import { useState } from "react";
import { icpAPI } from "../../lib/api";
import {
  INDUSTRY_GROUPS, PERSONA_GROUPS, PLATFORM_GROUPS,
  PAIN_POINT_GROUPS, USE_CASE_GROUPS,
} from "./icp-options";
import { COUNTRY_GROUPS } from "./country-options";
import { MultiSelect, Dropdown, FieldLabel } from "./icp-multiselect";

const BG = "#06080F";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";
const ACCENT = "#2563EB";

const SIZE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1001-5000", "5000+"];
const REVENUE_OPTIONS = ["Pre-revenue", "<$1M", "$1M-$5M", "$5M-$10M", "$10M-$50M", "$50M-$100M", "$100M-$500M", "$500M+"];
const AOV_OPTIONS = ["<$10", "$10-$50", "$50-$100", "$100-$500", "$500-$1K", "$1K-$5K", "$5K-$50K", "$50K+", "Subscription", "Usage-based"];
const REGION_OPTIONS = ["North America", "Europe", "Asia Pacific", "Latin America", "Middle East", "Africa", "Global"];
const BIZ_MODEL_OPTIONS = ["B2B SaaS", "B2C SaaS", "Marketplace", "Platform", "API-first", "Freemium", "Enterprise", "Usage-based", "Hybrid"];

const SectionTitle = ({ step, total, title, subtitle }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em", marginBottom: 8 }}>
      STEP {step} OF {total}
    </div>
    <h2 style={{ fontSize: 20, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em", marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>{title}</h2>
    <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.6 }}>{subtitle}</p>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div style={{ marginBottom: 22 }}>
    <FieldLabel hint={hint}>{label}</FieldLabel>
    {children}
  </div>
);

export default function InferenceICPSetup({ onComplete, existingProfile }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [industries, setIndustries] = useState(existingProfile?.target_industries || []);
  const [sizeMin, setSizeMin] = useState(existingProfile?.company_size_min || "");
  const [sizeMax, setSizeMax] = useState(existingProfile?.company_size_max || "");
  const [revenue, setRevenue] = useState(existingProfile?.revenue_range || "");
  const [aov, setAov] = useState(existingProfile?.average_order_value || "");
  const [regions, setRegions] = useState(existingProfile?.target_regions || []);
  const [countries, setCountries] = useState(existingProfile?.target_countries || []);
  const [bizModels, setBizModels] = useState(existingProfile?.business_models || []);
  const [techStack, setTechStack] = useState(existingProfile?.tech_stack || []);
  const [platforms, setPlatforms] = useState(existingProfile?.target_platforms || []);
  const [personaTitles, setPersonaTitles] = useState(
    (existingProfile?.target_personas || []).map(p => (typeof p === "string" ? p : p?.title)).filter(Boolean),
  );
  const [painPoints, setPainPoints] = useState(existingProfile?.pain_points || []);
  const [useCases, setUseCases] = useState(existingProfile?.use_cases || []);

  const TOTAL_STEPS = 5;

  const handleSave = async () => {
    setSaving(true);
    try {
      const profile = {
        target_industries: industries,
        company_size_min: sizeMin || null,
        company_size_max: sizeMax || null,
        revenue_range: revenue || null,
        average_order_value: aov || null,
        target_regions: regions,
        target_countries: countries,
        business_models: bizModels,
        tech_stack: techStack,
        target_platforms: platforms,
        target_personas: personaTitles.map(t => ({ title: t, department: "" })),
        pain_points: painPoints,
        use_cases: useCases,
      };
      if (existingProfile) await icpAPI.update(profile);
      else await icpAPI.save(profile);
      if (onComplete) onComplete();
    } catch (err) {
      console.error("Failed to save ICP:", err);
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div>
          <SectionTitle step={1} total={TOTAL_STEPS} title="Target Industries" subtitle="Search and select the industries you sell to or want to analyze. This drives ICP fit scoring." />
          <Field label="TARGET INDUSTRIES" hint="Type to search 50+ industries, or add your own.">
            <MultiSelect testId="icp-industries" groups={INDUSTRY_GROUPS} value={industries} onChange={setIndustries}
              placeholder="Search industries…" allowCustom />
          </Field>
        </div>
      );
      case 1: return (
        <div>
          <SectionTitle step={2} total={TOTAL_STEPS} title="Company Profile" subtitle="Define the ideal company size, revenue, order value, and the geographies you target." />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Field label="COMPANY SIZE (MIN)">
              <Dropdown testId="size-min-select" value={sizeMin} onChange={setSizeMin} options={SIZE_OPTIONS}
                formatOption={s => `${s} employees`} />
            </Field>
            <Field label="COMPANY SIZE (MAX)">
              <Dropdown testId="size-max-select" value={sizeMax} onChange={setSizeMax} options={SIZE_OPTIONS}
                formatOption={s => `${s} employees`} />
            </Field>
          </div>
          <Field label="REVENUE RANGE">
            <Dropdown testId="revenue-select" value={revenue} onChange={setRevenue} options={REVENUE_OPTIONS} />
          </Field>
          <Field label="TARGET AVERAGE ORDER VALUE" hint="We detect and compare order/contract values from analyzed companies.">
            <Dropdown testId="aov-select" value={aov} onChange={setAov} options={AOV_OPTIONS} />
          </Field>
          <Field label="TARGET REGIONS" hint="Broad geographic buckets. Leave empty if you sell globally.">
            <MultiSelect testId="icp-regions" options={REGION_OPTIONS} value={regions} onChange={setRegions}
              placeholder="Search regions…" />
          </Field>
          <Field label="TARGET COUNTRIES (OPTIONAL)" hint="Drill into specific countries. When set, these take priority over regions in geography scoring.">
            <MultiSelect testId="icp-countries" groups={COUNTRY_GROUPS} value={countries} onChange={setCountries}
              placeholder="Search countries…" groupSelectAll allowCustom />
          </Field>
        </div>
      );
      case 2: return (
        <div>
          <SectionTitle step={3} total={TOTAL_STEPS} title="Business Model, Platforms & Tech" subtitle="What business models and technologies define your ideal customer?" />
          <Field label="BUSINESS MODELS">
            <MultiSelect testId="icp-biz-models" options={BIZ_MODEL_OPTIONS} value={bizModels} onChange={setBizModels}
              placeholder="Search business models…" allowCustom />
          </Field>
          <Field label="TARGET PLATFORMS" hint="Companies running these score higher on the Tech Stack dimension and surface platform-match signals.">
            <MultiSelect testId="icp-platforms" groups={PLATFORM_GROUPS} value={platforms} onChange={setPlatforms}
              placeholder="Search platforms…" />
          </Field>
          <Field label="TECH STACK" hint="Any other tooling that signals a good fit. Add your own freely.">
            <MultiSelect testId="icp-tech-stack" groups={PLATFORM_GROUPS} value={techStack} onChange={setTechStack}
              placeholder="e.g. Stripe, AWS, Shopify…" allowCustom />
          </Field>
        </div>
      );
      case 3: return (
        <div>
          <SectionTitle step={4} total={TOTAL_STEPS} title="Target Personas" subtitle="Who are you selling to? Select the decision-makers and influencers in your target accounts." />
          <Field label="TARGET PERSONAS" hint="30+ presets grouped by department — or add a title of your own.">
            <MultiSelect testId="icp-personas" groups={PERSONA_GROUPS} value={personaTitles} onChange={setPersonaTitles}
              placeholder="Search personas…" allowCustom />
          </Field>
        </div>
      );
      case 4: return (
        <div>
          <SectionTitle step={5} total={TOTAL_STEPS} title="Pain Points & Use Cases" subtitle="What problems does your product solve? This sharpens hypotheses and buying signals. Optional." />
          <Field label="PAIN POINTS" hint="Pick from common pains or add the exact wording you use with customers.">
            <MultiSelect testId="icp-pain-points" groups={PAIN_POINT_GROUPS} value={painPoints} onChange={setPainPoints}
              placeholder="e.g. High checkout abandonment…" allowCustom chipColor="#EF4444" />
          </Field>
          <Field label="USE CASES" hint="How your product gets used, and in which scenarios.">
            <MultiSelect testId="icp-use-cases" groups={USE_CASE_GROUPS} value={useCases} onChange={setUseCases}
              placeholder="e.g. Payment optimization…" allowCustom chipColor="#22C55E" />
          </Field>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div data-testid="icp-setup-page" style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div style={{ maxWidth: 680, margin: "0 auto", padding: "48px 28px", width: "100%" }}>
        <div style={{ display: "flex", gap: 4, marginBottom: 40 }}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? ACCENT : BORDER, transition: "background 0.3s" }} />
          ))}
        </div>

        {!existingProfile && step === 0 && (
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.03em", marginBottom: 8 }}>
              Set up your Ideal Customer Profile
            </h1>
            <p style={{ fontSize: 14, color: TEXT_MUTED, lineHeight: 1.7, maxWidth: 480, margin: "0 auto" }}>
              This takes 2 minutes and dramatically improves the accuracy of your analysis Fit Scores, hypotheses, and buying signals.
            </p>
          </div>
        )}

        <div style={{ animation: "fadeIn 0.3s ease" }} key={step}>
          {renderStep()}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 36, paddingTop: 20, borderTop: `1px solid ${BORDER}` }}>
          <button data-testid="icp-back-btn" onClick={() => step > 0 && setStep(step - 1)}
            style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 20px", fontSize: 13, color: step > 0 ? TEXT_MUTED : TEXT_DIM, cursor: step > 0 ? "pointer" : "default", fontFamily: "'DM Sans', sans-serif", opacity: step > 0 ? 1 : 0.4 }}>
            Back
          </button>
          <div style={{ display: "flex", gap: 10 }}>
            {!existingProfile && (
              <button data-testid="icp-skip-btn" onClick={handleSave}
                style={{ background: "none", border: "none", padding: "10px 16px", fontSize: 13, color: TEXT_DIM, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                Skip for now
              </button>
            )}
            {step < TOTAL_STEPS - 1 ? (
              <button data-testid="icp-next-btn" onClick={() => setStep(step + 1)}
                style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                Continue
              </button>
            ) : (
              <button data-testid="icp-save-btn" onClick={handleSave} disabled={saving}
                style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 13, fontWeight: 600, cursor: saving ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : (existingProfile ? "Update Profile" : "Complete Setup")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
